// =============================================================================
//  Copyright   : 2018 semantics GmbH
//  Date        : $Date: 2025-09-21 19:16:35 +0200 (Sun, 21 Sep 2025) $
//  Author      : Dirk Rothe
//  Worker      : $Author: d.rothe $
//  Revision    : $Rev: 156393 $
//  Purpose     : call tree apis
// =============================================================================
// ---Third-party imports
import {
  put,
  takeEvery,
  take,
  call,
  actionChannel,
  throttle,
  all,
  fork,
  putResolve,
  race,
} from 'redux-saga/effects'
import {channel} from 'redux-saga'

// ---Framework imports
import {Actions, Sel} from 'uix/registries'
import {
  has,
  get,
  range,
  uniq,
  filterItems,
  dict,
  size,
  groupBy,
  entries,
  mapItems,
  keys,
  isNil,
  items,
  flatten,
  values,
} from 'ldash'
import Api from 'uix/api'
import {initializeDomain, select, buffers, matchServerEvent, delay} from 'uix/sagas'
import log from 'uix/logging'
import {selectAncestorDomains, getRunId} from 'uix/selectors'
import {hashObj, encodeSlimLink, isSSR} from 'uix/utils'
import {normalizeByDatatype, normalize} from 'uix/normalize'
import {
  listTree,
  initializeLinkDomains,
  initializeRecordDomains,
  trackActivityCall,
} from 'uix/db/sagas'

// ---Local imports
import {ID, getTreeTplClasses} from './utils'

import {
  setTreeChildren,
  setTreeChildrenLoading,
  //~ setTreeChildrenActive,
  setTreeDetails,
  setTreeDetailsErrors,
  fetchDescendants,
  setDescendants,
  setTreeNodeData,
  deleteTreeRecords,
  getTreeNodes,
  fetchAncestorAxisChildren,
} from './actions'
import {
  ancestorAxis2TreeAxis,
  rules,
  NTYPE,
  DATATYPES_EXPAND_LOOKUP,
  getUnitializedRootDomains,
} from './rules'

// ---Globals
function* _deleteTreeRecords({payload}) {
  let {otid, otids, erase, onDeleted} = payload
  if (otid) {
    otids = [otid]
  }
  let {data, error} = yield Api.post(`/api/deleteTreeRecords`, {otids: otids.join(','), erase})
  if (data) {
    if (size(data.deleted) > 0) {
      yield put(Actions.successNotification({message: `Deleted ${size(data.deleted)} records.`}))
      if (onDeleted) {
        onDeleted()
      }
    }
    if (size(data.failures) > 0) {
      for (let [failtype, records] of items(data.failures)) {
        yield put(Actions.warningNotification({message: `${failtype}: ${size(records)} records.`}))
      }
    }
  } else if (error) {
    yield put(Actions.errorNotification({error, header: 'Delete Records'}))
  }
}

function* _fetchDescendants({payload}) {
  let {node, typeNames, stateNames} = payload
  let typeName = typeNames.length > 0 ? typeNames.join(',') : 'metadata'
  let {otid, odid, idContext} = node
  let {data, error} = yield listTree({
    parentid: otid,
    odid,
    typeName,
    stateNames,
    descendants: true,
  })
  if (error) {
    log.error('list tree failed (descendants): %o', error)
  } else {
    yield initializeLinkDomains(data)
    yield put(setDescendants(node, data, typeNames))
  }
}

function* getChildren({payload}) {
  let {
    otid,
    odid,
    idContext,
    typeName,
    stateNames,
    rootFolder,
    deleted,
    descendants,
    subdomains,
    links,
  } = payload

  let loadKey = hashObj(payload)
  let isLoading = yield select(root => root.data.tree.loading[loadKey])
  if (isLoading) {
    log.debug('children already loading...')
    return
  }
  yield putResolve(setTreeChildrenLoading(loadKey, true))
  //~ if (otid) {
  // for refresh tracking (needs some "is really used" handling)
  //~ yield put(setTreeChildrenActive(idContext, otid, payload))
  //~ }

  let {data, error, meta} = yield listTree({
    parentid: otid,
    odid,
    typeName,
    stateNames,
    rootFolder,
    deleted,
    descendants,
    subdomains,
    links,
  })

  if (error) {
    log.error('list tree failed: %o', error)
  } else {
    if (links) {
      // initialize domains of linked nodes
      yield initializeLinkDomains(data)
    }

    if (meta.slimlinks) {
      data = data.map(d =>
        d.otid == d.otlink ? {...d, slimlink: encodeSlimLink(otid, d.otid), otlink: null} : d
      )
    }

    if (subdomains) {
      // initialize domains of subdomain records
      yield initializeRecordDomains(data)
    }
    // update tree in store
    let dname = yield select(root => root.config.domains.odid2name[odid])
    yield putResolve(setTreeChildren(otid, data, idContext, payload))
    yield putResolve(setTreeChildrenLoading(loadKey, false))
  }
}

function* watchGetTreeChildrenSerial(chan) {
  /* process getChildren requests serially */
  let requestChan = chan
  if (!requestChan) {
    requestChan = yield actionChannel('GET_TREE_CHILDREN', buffers.expanding(10))
  }
  while (true) {
    const action = yield take(requestChan)
    yield call(trackActivityCall, 'GET_TREE_CHILDREN', getChildren, action)
    //~ yield call(getChildren, action)
  }
}

function* watchGetTreeChildrenParallel(numWorkers) {
  /* process getChildren requests parallel */
  const chan = yield call(channel)

  for (let i of range(numWorkers)) {
    yield fork(watchGetTreeChildrenSerial, chan)
  }

  while (true) {
    const action = yield take('GET_TREE_CHILDREN')
    yield put(chan, action)
  }
}

function* initializeDomains(dname, domains) {
  /* load missing domain config with respect to odid (ancestors)
  and tree config (visible root nodes)
  */
  let user = yield select(Sel.user)
  let iconfig = yield select(Sel.iconfig)
  let loadedDomains = yield select(root => root.config.ddata)
  let ancestorDomains = yield select(selectAncestorDomains(dname))

  let unitializedDomains = []
  if (!loadedDomains[dname]) {
    // this should normally not happen (but happens if otid is initialized on wrong domain)
    // make sure our domain is also loaded
    unitializedDomains.push(dname)
  }

  for (let domain of ancestorDomains) {
    if (!domain.isGhost && !loadedDomains[domain.name]) {
      unitializedDomains.push(domain.name)
    }
  }
  let unitializedRootDomains = getUnitializedRootDomains(iconfig, domains, loadedDomains, user)
  for (let dname of uniq([...unitializedDomains, ...unitializedRootDomains])) {
    yield put(Actions.initDomain(dname, 'tree ancestors'))
  }
}

function* ensureInitializedDomains(odids, reason) {
  odids = [...new Set(odids)]
  let loadedDomains = yield select(root => root.config.ddata)
  let domains = yield select(root => root.config.domains)
  let unitializedDomains = odids.filter(odid => !loadedDomains[odid])
  for (let odid of unitializedDomains) {
    yield put(Actions.initDomain(domains.odid2name[odid], reason))
  }
}

function* _getTreeNodes({payload}) {
  let {otids} = payload
  let {data, error} = yield listTree({otids})
  if (data) {
    yield ensureInitializedDomains(data.map(node => node.odid), 'some tree nodes')
    yield put(Actions.setTreeNodesData(data))
  } else if (error) {
    log.exception('error fetching otids', error)
  }
}

function* fetchAncestorAxis({payload}) {
  /* ensure tree nodes are loaded for given ancestor axis
  also ensure ancestor domains are initialized (node.loaded = true)

  XXX: VTree.ensureExpandedChildren also triggers data fetching
  - but only during the "big" tree-rebuild step
  */

  let {ctxid: request_ctxid, ancestorAxis} = payload

  let {odid, datatype, subtype} = ancestorAxis[0]
  let childData = yield select(root => root.data.tree.childData)
  let domains = yield select(root => root.config.domains)

  // copy over for faster generating node from request.id
  // XXX: replicated in useTreeNode hook
  let byId = yield select(root => root.data.tree.byId)
  // XXX: do we really need to do this?
  if (!byId[ancestorAxis[0].otid]) {
    let {otid, caption, delstate} = ancestorAxis[0]
    yield put(setTreeNodeData(otid, odid, caption, datatype, subtype, delstate))
  }

  if (DATATYPES_EXPAND_LOOKUP.includes(datatype)) {
    let treeAxis = ancestorAxis2TreeAxis(ancestorAxis, domains)

    // if we are in rootOverride mode due to additional ctxid request param
    // ensure our children are also fetched
    if (!request_ctxid) {
      // remove last node
      treeAxis = treeAxis.slice(0, -1)
    }

    for (let {nodeType, idContext, otid, odid} of treeAxis) {
      if (!idContext || otid === undefined) {
        continue
      }

      let missing = !childData[idContext] || !childData[idContext][otid]
      if (missing) {
        let getChildrenAction = rules({treeById: byId})[nodeType].getChildren
        if (getChildrenAction) {
          let node = {otid, odid, idContext}
          //~ log.debug('getTree %o', node)

          yield getChildren(getChildrenAction(node))
        }
      }
    }
  }
}

function* goTreeThrottled({payload}) {
  /* dispatch to standard go() */
  yield put(Actions.go(payload))
}

const TREE_DETAIL_FIELDS = ['oc.sourceident', 'pdf.id', 'ocr.id', 'haschildren']
function* getDetailsThrottled({payload}) {
  /*Generator function that fetches tree data details while handling errors
    and preventing redundant requests. It accesses the application state to
    filter out IDs with errors, groups them by data type, and makes an API call
    for the necessary fields. Handles response errors, logs missing
    data issues, and updates the Redux store with normalized results. It flags
    failed fetches to prevent retrying.
   */
  let tree = yield select(root => root.data.tree)
  let {loadingDetails, detailsLoadErrors} = tree

  // do not refetch failures - detailsLoadErrors contains int values
  let noErrors = l => l.filter(([otid, dt]) => isNil(detailsLoadErrors[otid]))

  let fetchIds = noErrors(entries(loadingDetails))
  let byDatatype = groupBy(fetchIds, e => e[1])

  if (size(byDatatype) == 0) {
    return
  }

  byDatatype = dict(mapItems(byDatatype, (k, v) => [k, v.map(e => e[0])]))
  let selectFields = TREE_DETAIL_FIELDS.join(',')
  let {data, error} = yield Api.post(`/api/treeFields`, {
    byDatatype: JSON.stringify(byDatatype),
    select: selectFields,
  })

  if (error || (data && data.byDatatype === undefined)) {
    log.error('error during fetch details')
  } else {
    let normalized = normalizeByDatatype(data.byDatatype, data.meta)
    if (size(fetchIds) !== size(normalized)) {
      let retrieved = new Set(normalized.map(r => parseInt(r.otid)))
      let missing = dict(fetchIds.filter(([otid]) => !retrieved.has(parseInt(otid))))
      // flag ids as failed, so we will not retry
      log.warn('tree detail load errors %o - missing: %o', byDatatype, keys(missing).slice(0, 10))
      yield put(setTreeDetailsErrors(missing))
    }
    // also put partial results
    yield put(setTreeDetails(normalized))
  }
}

const GO_TREE_THROTTLE_MS = 500
const DETAILS_TREE_THROTTLE_MS = 300

let loopers = {}

//~ function* watchFetchDescendants() {
//~ let chan = yield actionChannel('FETCH_TREE_DESCENDANTS', buffers.fixed(10))
//~ while (true) {
//~ let action = yield take(chan)
//~ yield call(trackActivityCall, 'FETCH_TREE_DESCENDANTS', _fetchDescendants, action)
//~ }
//~ }
//~ const WATCH_EVENTS = ['ERecordInserted', 'ERecordMoved']
//~ const THROTTLE_REFETCH = 1000

//~ function* watchTreeServerDataChanged() {
//~ /* watch for WATCH_EVENTS
//~ refetch children via tree.childrenActive
//~ */
//~ return
//~ let invalidated_otids = []
//~ try {
//~ while (true) {
//~ let take_evts = dict(WATCH_EVENTS.map(e => [e, take(matchServerEvent(e))]))
//~ let {timeout, ...took} = yield race({timeout: delay(THROTTLE_REFETCH), ...take_evts})

//~ // normalize to empty lists
//~ let evts = dict(WATCH_EVENTS.map(e => [e, took[e] ? [took[e].payload] : []]))
//~ // extract otids
//~ let event_otids = [
//~ ...[...evts.ERecordInserted, ...evts.ERecordMoved].map(evt => evt.otparent),
//~ ...evts.ERecordMoved.map(evt => evt.payload.otparent_old),
//~ ]
//~ if (timeout) {
//~ // after timeout process collected otids
//~ if (invalidated_otids.length > 0) {
//~ let otids = uniq(invalidated_otids).map(otid => String(otid))
//~ let childrenActive = yield select(root => root.data.tree.childrenActive)
//~ let getChildrenParams = flatten(
//~ mapItems(childrenActive, (idContext, otid2params) =>
//~ mapItems(otid2params, (otid, params) => params, otid => otids.includes(otid))
//~ )
//~ )

//~ // log.x({otids, childrenActive, getChildrenParams})

//~ invalidated_otids = []
//~ }
//~ } else if (event_otids.length > 0) {
//~ invalidated_otids.push(...event_otids)
//~ }
//~ }
//~ } catch (e) {
//~ log.exception(e)
//~ }
//~ }

if (!isSSR) {
  // maybe the simple serial solution is enough
  //~ watchGetTreeChildren: watchGetTreeChildrenSerial(),
  loopers.watchGetTreeChildren = watchGetTreeChildrenParallel(10)
} else {
  // SSR fallback
  loopers.getChildren = takeEvery('GET_TREE_CHILDREN', getChildren)
}

export const sagas = {
  fetchAncestorAxis: takeEvery(fetchAncestorAxisChildren, fetchAncestorAxis),
  // if we navigate the tree with keyboard we can get many go actions per sec
  // process only one each 500 ms
  goTreeThrottled: throttle(GO_TREE_THROTTLE_MS, 'GO_TREE', goTreeThrottled),
  getDetailsThrottled: throttle(DETAILS_TREE_THROTTLE_MS, 'GET_TREE_DETAILS', getDetailsThrottled),

  getAvailableTypes: takeEvery('GET_AVAILABLE_TYPES', function*({payload}) {
    let {dname} = payload
    let {data, error} = yield Api.get(`/${dname}/api/getAvailableTypes`)
    if (data) {
      yield put(Actions.setAvailableTypes({dname, data}))
    }
  }),

  getAvailableStates: takeEvery('GET_AVAILABLE_STATES', function*({payload}) {
    let {dname, statusType} = payload
    let {data, error} = yield Api.get(`/${dname}/api/getStateCounts`, {
      statusType,
      subdomains: true,
    })
    if (data) {
      yield put(Actions.setAvailableStates({dname, statusType, data}))
    }
  }),
  getDelivererYears: takeEvery('GET_DELIVERER_YEARS', function*({payload}) {
    let {otid, idContext} = payload
    let {data, error} = yield Api.get(`/api/deliverer/getDeliveryYears`, {otid})
    if (data) {
      yield put(Actions.setDelivererYears({otid, idContext, data}))
    }
  }),
  getDelivererYearMds: takeEvery('GET_DELIVERER_YEAR_MDS', function*({payload}) {
    let {otid, year} = payload
    let {data, error} = yield Api.get(`/api/deliverer/getDeliveryYearMds`, {otid, year})
    if (data) {
      yield put(Actions.setDelivererYearMds({otid, year, md_nodes: normalize(data.mds)}))
    }
  }),

  watchNodeInsert: takeEvery('TREE_NODE_CREATED', function*({payload}) {
    /* rerun getChildren (for parent id) if tree node is created on server
     */
    let childDataControl = yield select(root => root.data.tree.childDataControl)
    // XXX: we need only to to this for expanded tree nodes
    // here we refresh for all nodes that also where once open
    let refreshable = flatten(
      values(childDataControl).map(otid2params => values(otid2params))
    ).filter(params => params.otid == payload.parent_otid)
    for (let params of refreshable) {
      yield put(Actions.getChildren(params))
    }
    //~ vl.pprint({parent_otid, refreshable, childDataControl})
  }),

  deleteTreeRecords: takeEvery(deleteTreeRecords, _deleteTreeRecords),

  getTreeNodes: takeEvery(getTreeNodes, _getTreeNodes),

  // XXX: tried to use this on changing nodeFilter.js - but changes are not registered from VTree.getDerivedStateFromProps
  //~ hotreloadTreeDataVersion: takeEvery('HOT_RELOAD', function*({payload}) {
  //~ if (payload.status == 'idle') {
  //~ yield put({type: 'TREE_RECALC', payload: {}})
  //~ }
  //~ }),

  //~ fetchDescendants: takeEvery(fetchDescendants, _fetchDescendants),

  //~ watchFetchDescendants: watchFetchDescendants(),
  //~ subscribeNodeChangedEvents: takeEvery('SET_USER', function*({payload}) {
  //~ // not in SSR
  //~ if (isSSR) return

  //~ let {user} = payload

  //~ if (user.puid) {
  //~ yield put(Actions.subscribeServerEvent(WATCH_EVENTS, true))
  //~ }
  //~ }),
  //~ watchTreeServerDataChanged: watchTreeServerDataChanged(),
  ...loopers,
}
